'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { MediaSlot } from '@/components/ui/MediaSlot';
import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface HowItWorksSectionProps {
  isLoading?: boolean;
}

const steps = [
  {
    number: 1,
    title: 'Your agent writes the code',
    description:
      'Claude Code, Codex, Copilot, or another agent writes the change. Mindrealm reviews the output regardless of which agent produced it.',
  },
  {
    number: 2,
    title: 'Mindrealm reviews it',
    description:
      'After Claude Code, Codex, Kimi Code, or Antigravity CLI finishes, on every pull request, or on demand from your terminal. The same code produces the same findings.',
  },
  {
    number: 3,
    title: 'The agent fixes the findings',
    description:
      'On Claude Code, Codex, Kimi Code, and Antigravity CLI, the agent fixes what it can. Mindrealm checks the change again, and the loop closes when no findings remain.',
  },
];

export function HowItWorksSection({
  isLoading = false,
}: HowItWorksSectionProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  if (isLoading || !mounted) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1200px]">
          <Skeleton className="mx-auto mb-4 h-10 w-[45%]" />
          <Skeleton className="mx-auto mb-10 h-5 w-[60%]" />
          <div className="mb-10 grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-40 rounded-xl" />
            ))}
          </div>
          <Skeleton className="mx-auto aspect-video w-full max-w-[900px] rounded-xl" />
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div className="mx-auto max-w-[1200px]">
        <h2
          className="text-gradient-hero mb-4 text-center text-3xl font-bold tracking-tight md:text-4xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          Review. Fix. Verify.
        </h2>
        <p
          className="text-foreground/70 mb-10 text-center text-base md:text-lg"
          style={{ fontFamily: theme.fonts.body }}
        >
          Generation is fast. Verification is the missing half.
          <br />
          On Kimi Code, Antigravity CLI, Claude Code, and Codex, Mindrealm runs
          when the agent finishes so it sees the findings before you do.
        </p>

        <div className="mb-12 grid gap-6 md:grid-cols-3">
          {steps.map((step) => (
            <div
              key={step.number}
              className="rounded-xl p-6"
              style={{
                background: 'transparent',
                border: `1px solid ${theme.box.outline.border[isDark ? 'dark' : 'light']}`,
              }}
            >
              <div className="mb-2 flex items-center gap-3">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg font-bold"
                  style={{
                    background:
                      theme.box.accentNumber.bg[isDark ? 'dark' : 'light'],
                    color:
                      theme.box.accentNumber.color[isDark ? 'dark' : 'light'],
                  }}
                >
                  {step.number}
                </span>
                <h3
                  className="text-lg font-semibold md:text-xl"
                  style={{ fontFamily: theme.fonts.display }}
                >
                  {step.title}
                </h3>
              </div>
              <p
                className="text-foreground/70 text-sm md:text-base"
                style={{ fontFamily: theme.fonts.body }}
              >
                {step.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mx-auto max-w-[900px]">
          <MediaSlot assetKey="heroDemoVideo" />
        </div>
      </div>
    </section>
  );
}
